import { z } from 'zod'

export type JsonValue =
  | string
  | number
  | boolean
  | null
  | JsonValue[]
  | { [key: string]: JsonValue }

/**
 * A JSON value `JSON.stringify` keeps. Object values may be `undefined` so a
 * type with optional fields (a Zod input) is assignable; `JSON.stringify` drops
 * those keys. `JsonValue` cannot express that, because its index signature
 * forbids `undefined`.
 */
export type JsonBody =
  | string
  | number
  | boolean
  | null
  | readonly JsonBody[]
  | { readonly [key: string]: JsonBody | undefined }

/** A JSON object body. */
export type JsonObject = { readonly [key: string]: JsonBody | undefined }
export const jsonValueSchema: z.ZodType<JsonValue> = z.lazy(() =>
  z.union([
    z.string(),
    z.number(),
    z.boolean(),
    z.null(),
    z.array(jsonValueSchema),
    z.record(z.string(), jsonValueSchema),
  ]),
)

/**
 * A JSON object whose values are all strings. Envelope metadata and audit
 * payloads use this on the wire; the column itself is untyped JSON.
 */
export const stringRecordSchema = z.record(z.string(), z.string())

export type StringRecord = z.infer<typeof stringRecordSchema>

/** Parse a text body as JSON. Empty input is `null`; invalid JSON throws. */
export function parseJsonText(text: string): JsonValue | null {
  if (text.trim() === '') {
    return null
  }
  return jsonValueSchema.parse(JSON.parse(text))
}

export const originUrlSchema = z.string().url().refine((value) => {
  try {
    return new URL(value).origin === value
  } catch {
    return false
  }
}, 'Must be an origin (scheme + host, no path)')
